import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const FONT_SIZE_PX = 36;
/** Small inset so glyphs are not clipped at the SVG bounds (not a large draggable “dead zone”). */
const EDGE_PAD_PX = 2;
/** Default inset from slot edge when x is inferred (similar to legacy 16px start). */
const DEFAULT_INSET = 16;

export type OverlayTextHorizontalAlign = 'start' | 'middle' | 'end';

/** Map pointer to SVG viewBox coords (uniform scale fit). */
export function pointerToSvgCoords(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const rect = svg.getBoundingClientRect();
  const vb = svg.viewBox.baseVal;
  const w = vb.width || Number.parseFloat(svg.getAttribute('width') || '') || rect.width;
  const h = vb.height || Number.parseFloat(svg.getAttribute('height') || '') || rect.height;
  return {
    x: ((clientX - rect.left) / Math.max(rect.width, 1)) * w,
    y: ((clientY - rect.top) / Math.max(rect.height, 1)) * h,
  };
}

export function clampOverlayTextPosition(
  pos: { x: number; y: number },
  width: number,
  height: number,
  textWidth: number,
  textHalfHeight: number,
  align: OverlayTextHorizontalAlign,
): { x: number; y: number } {
  let { x, y } = pos;
  const pad = EDGE_PAD_PX;
  const wSafe = Math.max(textWidth, 1);
  const hHalf = Math.max(textHalfHeight, 1);
  if (align === 'start') {
    x = Math.max(pad, Math.min(width - pad - wSafe, x));
  } else if (align === 'end') {
    x = Math.max(pad + wSafe, Math.min(width - pad, x));
  } else {
    const halfW = wSafe / 2;
    x = Math.max(pad + halfW, Math.min(width - pad - halfW, x));
  }
  y = Math.max(pad + hHalf, Math.min(height - pad - hHalf, y));
  return { x, y };
}

function svgTextAnchorForAlign(align: OverlayTextHorizontalAlign): 'start' | 'middle' | 'end' {
  return align;
}

function defaultAnchorForAlign(
  align: OverlayTextHorizontalAlign,
  width: number,
  height: number,
): { x: number; y: number } {
  switch (align) {
    case 'end':
      return { x: width - DEFAULT_INSET, y: height / 2 };
    case 'middle':
      return { x: width / 2, y: height / 2 };
    default:
      return { x: DEFAULT_INSET, y: height / 2 };
  }
}

export interface OverlayTextSvgProps {
  text: string;
  width: number;
  height: number;
  fontFamily: string;
  /** Horizontal alignment anchor for `<text>` and default x when `position` is null. */
  horizontalAlign?: OverlayTextHorizontalAlign;
  /** Persisted anchor; omit or null to use default placement. */
  position: { x: number; y: number } | null;
  onPositionChange?: (next: { x: number; y: number }) => void;
  /** Live preview only — export capture stays non-interactive. */
  interactive?: boolean;
}

/**
 * Plain SVG text overlay; optional drag in preview (similar pan affordance as molecules).
 */
export function OverlayTextSvg({
  text,
  width,
  height,
  fontFamily,
  horizontalAlign = 'start',
  position,
  onPositionChange,
  interactive = false,
}: OverlayTextSvgProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const textMeasureRef = useRef<SVGTextElement>(null);
  const dragRef = useRef<{ pointerId: number; grabDx: number; grabDy: number } | null>(null);

  /** Measured SVG text extents (avoids overstating width vs label-length heuristics). */
  const [textMetrics, setTextMetrics] = useState<{ w: number; halfH: number }>(() => ({
    w: FONT_SIZE_PX,
    halfH: FONT_SIZE_PX * 0.5,
  }));

  useLayoutEffect(() => {
    const el = textMeasureRef.current;
    if (!el) return;
    try {
      const tw = el.getComputedTextLength();
      const bh = typeof el.getBBox === 'function' ? el.getBBox().height : 0;
      setTextMetrics({
        w: Number.isFinite(tw) && tw > 0 ? tw : FONT_SIZE_PX,
        halfH:
          Number.isFinite(bh) && bh > 0
            ? bh / 2
            : FONT_SIZE_PX * 0.52,
      });
    } catch {
      setTextMetrics({
        w: FONT_SIZE_PX,
        halfH: FONT_SIZE_PX * 0.52,
      });
    }
  }, [text, fontFamily, width, height, horizontalAlign]);

  const defaultAnchor = defaultAnchorForAlign(horizontalAlign, width, height);
  const anchor = position ?? defaultAnchor;

  const emitDrag = useCallback(
    (p: { x: number; y: number }) => {
      if (!onPositionChange) return;
      onPositionChange(
        clampOverlayTextPosition(p, width, height, textMetrics.w, textMetrics.halfH, horizontalAlign),
      );
    },
    [onPositionChange, width, height, textMetrics.w, textMetrics.halfH, horizontalAlign],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<SVGTextElement>) => {
      if (!interactive || !onPositionChange || e.button !== 0) return;
      e.stopPropagation();
      const svg = svgRef.current;
      if (!svg) return;
      const pt = pointerToSvgCoords(svg, e.clientX, e.clientY);
      dragRef.current = {
        pointerId: e.pointerId,
        grabDx: anchor.x - pt.x,
        grabDy: anchor.y - pt.y,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.style.cursor = 'grabbing';
    },
    [interactive, onPositionChange, anchor.x, anchor.y],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGTextElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId || !interactive || !onPositionChange) return;
      const svg = svgRef.current;
      if (!svg) return;
      const pt = pointerToSvgCoords(svg, e.clientX, e.clientY);
      emitDrag({ x: pt.x + drag.grabDx, y: pt.y + drag.grabDy });
    },
    [interactive, onPositionChange, emitDrag],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<SVGTextElement>) => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
      e.currentTarget.style.cursor = 'grab';
    }
  }, []);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
    >
      <text
        ref={textMeasureRef}
        x={anchor.x}
        y={anchor.y}
        textAnchor={svgTextAnchorForAlign(horizontalAlign)}
        dominantBaseline="middle"
        fontSize={FONT_SIZE_PX}
        fontFamily={fontFamily}
        fill="rgb(31 41 55)"
        style={{
          cursor: interactive && onPositionChange ? 'grab' : undefined,
          touchAction: interactive ? 'none' : undefined,
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {text}
      </text>
    </svg>
  );
}
